/**
 * Questionnaire Service
 * 
 * Handles all Firestore operations for the Scroll-i-telling questionnaire module:
 * - Questionnaire templates (admin creates)
 * - Assignments (admin assigns to team members)
 * - Responses (team members fill out with auto-save)
 */

import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy, 
  Timestamp 
} from 'firebase/firestore';
import type { 
  Questionnaire, 
  QuestionnaireAssignment, 
  QuestionnaireResponse,
  QuestionnaireEvent
} from '../types/questionnaires';

export const questionnaireService = {
  /**
   * ========================================
   * QUESTIONNAIRE CRUD (Admin only)
   * ========================================
   */
  
  /**
   * Create a new questionnaire template
   */
  async createQuestionnaire(data: Omit<Questionnaire, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'questionnaires'), {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    console.log('✅ Questionnaire created:', docRef.id);
    return docRef.id;
  },

  /**
   * Get a specific questionnaire by ID
   */
  async getQuestionnaire(id: string): Promise<Questionnaire | null> {
    const docSnap = await getDoc(doc(db, 'questionnaires', id));
    if (!docSnap.exists()) return null;
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Questionnaire;
  },

  /**
   * List all questionnaires (admin view)
   */
  async listQuestionnaires(): Promise<Questionnaire[]> {
    const q = query(collection(db, 'questionnaires'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    } as Questionnaire));
  },

  /**
   * Update a questionnaire
   */
  async updateQuestionnaire(id: string, data: Partial<Questionnaire>): Promise<void> {
    await updateDoc(doc(db, 'questionnaires', id), {
      ...data,
      updatedAt: Timestamp.now()
    });
  },

  /**
   * Delete a questionnaire
   * WARNING: This is destructive and should only be used for cleanup
   */
  async deleteQuestionnaire(id: string): Promise<void> {
    await deleteDoc(doc(db, 'questionnaires', id));
    console.log('✅ Questionnaire deleted:', id);
  },

  /**
   * Duplicate a questionnaire (creates a copy with new ID)
   */
  async duplicateQuestionnaire(id: string, newTitle?: string): Promise<string> {
    const original = await this.getQuestionnaire(id);
    if (!original) {
      throw new Error('Fragebogen nicht gefunden');
    }
    
    const copy = {
      title: newTitle || `${original.title} (Kopie)`,
      description: original.description,
      templateId: original.templateId,
      templateName: original.templateName,
      createdBy: original.createdBy,
      createdByName: original.createdByName
    };
    
    const newId = await this.createQuestionnaire(copy);
    console.log('✅ Questionnaire duplicated:', id, '→', newId);
    return newId;
  },

  /**
   * Archive a questionnaire (soft delete)
   */
  async archiveQuestionnaire(id: string): Promise<void> {
    await this.updateQuestionnaire(id, {
      archived: true,
      archivedAt: new Date()
    } as Partial<Questionnaire>);
    console.log('✅ Questionnaire archived:', id);
  },

  /**
   * Restore an archived questionnaire
   */
  async restoreQuestionnaire(id: string): Promise<void> {
    await this.updateQuestionnaire(id, {
      archived: false,
      archivedAt: undefined
    } as Partial<Questionnaire>);
    console.log('✅ Questionnaire restored:', id);
  },

  /**
   * ========================================
   * ASSIGNMENT CRUD
   * ========================================
   */

  /**
   * Create an assignment (admin assigns questionnaire to team member)
   */
  async createAssignment(data: Omit<QuestionnaireAssignment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'questionnaire_assignments'), {
      ...data,
      deadline: Timestamp.fromDate(data.deadline),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    console.log('✅ Assignment created:', docRef.id, 'for', data.assignedTo);
    return docRef.id;
  },

  /**
   * Get all assignments for a specific user (by email)
   */
  async getAssignmentsForUser(userEmail: string): Promise<QuestionnaireAssignment[]> {
    const q = query(
      collection(db, 'questionnaire_assignments'),
      where('assignedTo', '==', userEmail),
      orderBy('deadline', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      deadline: doc.data().deadline?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      submittedAt: doc.data().submittedAt?.toDate()
    } as QuestionnaireAssignment));
  },

  /**
   * Get a specific assignment by ID
   */
  async getAssignment(id: string): Promise<QuestionnaireAssignment | null> {
    const docSnap = await getDoc(doc(db, 'questionnaire_assignments', id));
    if (!docSnap.exists()) return null;
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      deadline: data.deadline?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      submittedAt: data.submittedAt?.toDate()
    } as QuestionnaireAssignment;
  },

  /**
   * Get all assignments for a specific questionnaire
   */
  async getAssignmentsByQuestionnaire(questionnaireId: string): Promise<QuestionnaireAssignment[]> {
    const q = query(
      collection(db, 'questionnaire_assignments'),
      where('questionnaireId', '==', questionnaireId)
      // Note: orderBy removed temporarily while index builds (5-10 min)
      // Add back: orderBy('createdAt', 'desc') once index is ready
    );
    const snapshot = await getDocs(q);
    
    const assignments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      deadline: doc.data().deadline?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      submittedAt: doc.data().submittedAt?.toDate()
    } as QuestionnaireAssignment));
    
    // Sort client-side while index builds
    return assignments.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  },

  /**
   * Update assignment fields (generic update)
   */
  async updateAssignment(
    assignmentId: string,
    updates: Partial<QuestionnaireAssignment>
  ): Promise<void> {
    const updateData: Record<string, unknown> = {
      ...updates,
      updatedAt: Timestamp.now()
    };
    
    // Convert Date objects to Timestamps
    if (updates.deadline) {
      updateData.deadline = Timestamp.fromDate(updates.deadline as Date);
    }
    if (updates.submittedAt) {
      updateData.submittedAt = Timestamp.fromDate(updates.submittedAt as Date);
    }
    
    await updateDoc(doc(db, 'questionnaire_assignments', assignmentId), updateData);
    console.log('✅ Assignment updated:', assignmentId);
  },

  /**
   * Update assignment status
   */
  async updateAssignmentStatus(
    assignmentId: string, 
    status: string, 
    submittedAt?: Date
  ): Promise<void> {
    const updates: Record<string, unknown> = {
      status,
      updatedAt: Timestamp.now()
    };
    
    if (submittedAt) {
      updates.submittedAt = Timestamp.fromDate(submittedAt);
    }
    
    await updateDoc(doc(db, 'questionnaire_assignments', assignmentId), updates);
    console.log('✅ Assignment status updated:', assignmentId, '→', status);
  },

  /**
   * Cancel an assignment with reason tracking
   * @param assignmentId Assignment ID
   * @param reason Reason for cancellation
   * @param cancelledByUserId User ID who cancelled
   * @param cancelledByUserName User name who cancelled
   */
  async cancelAssignment(
    assignmentId: string,
    reason: string,
    cancelledByUserId: string,
    cancelledByUserName: string
  ): Promise<void> {
    const updates: Record<string, unknown> = {
      status: 'cancelled',
      cancellationReason: reason,
      cancelledAt: Timestamp.now(),
      cancelledBy: cancelledByUserId,
      cancelledByName: cancelledByUserName,
      updatedAt: Timestamp.now()
    };
    
    await updateDoc(doc(db, 'questionnaire_assignments', assignmentId), updates);
    console.log(`✅ Assignment ${assignmentId} cancelled by ${cancelledByUserName}`);
  },

  /**
   * ========================================
   * RESPONSE CRUD (with auto-save)
   * ========================================
   */

  /**
   * Save or update a response (auto-save or final submit)
   */
  async saveResponse(
    data: Partial<QuestionnaireResponse>, 
    isAutoSave = true
  ): Promise<string> {
    // Remove undefined values (Firestore doesn't like them)
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([_, v]) => v !== undefined)
    );
    
    const responseData = {
      ...cleanData,
      lastSaved: Timestamp.now()
    };

    if (data.id) {
      // Update existing response
      await updateDoc(doc(db, 'questionnaire_responses', data.id), responseData);
      if (!isAutoSave) {
        console.log('✅ Response updated:', data.id);
      }
      return data.id;
    } else {
      // Create new response
      const docRef = await addDoc(collection(db, 'questionnaire_responses'), responseData);
      console.log('✅ Response created:', docRef.id);
      return docRef.id;
    }
  },

  /**
   * Get a user's response for a specific assignment
   */
  async getResponse(assignmentId: string, userId: string): Promise<QuestionnaireResponse | null> {
    const q = query(
      collection(db, 'questionnaire_responses'),
      where('assignmentId', '==', assignmentId),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const docSnap = snapshot.docs[0];
    const data = docSnap.data();
    
    return {
      id: docSnap.id,
      ...data,
      lastSaved: data.lastSaved?.toDate() || new Date(),
      submittedAt: data.submittedAt?.toDate()
    } as QuestionnaireResponse;
  },

  /**
   * Submit a response (marks as final)
   */
  async submitResponse(responseId: string, assignmentId: string): Promise<void> {
    // Update response
    await updateDoc(doc(db, 'questionnaire_responses', responseId), {
      isSubmitted: true,
      submittedAt: Timestamp.now(),
      lastSaved: Timestamp.now()
    });
    
    // Update assignment status
    await this.updateAssignmentStatus(assignmentId, 'submitted', new Date());
    
    console.log('✅ Response submitted:', responseId);
  },

  /**
   * Get all responses for a questionnaire (admin view)
   */
  async getResponsesByQuestionnaire(questionnaireId: string): Promise<QuestionnaireResponse[]> {
    const q = query(
      collection(db, 'questionnaire_responses'),
      where('questionnaireId', '==', questionnaireId)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      lastSaved: doc.data().lastSaved?.toDate() || new Date(),
      submittedAt: doc.data().submittedAt?.toDate()
    } as QuestionnaireResponse));
  },

  /**
   * ========================================
   * HELPER FUNCTIONS
   * ========================================
   */

  /**
   * Check if assignment is past deadline
   */
  isOverdue(assignment: QuestionnaireAssignment): boolean {
    return new Date() > new Date(assignment.deadline);
  },

  /**
   * Check if assignment is editable by team member
   */
  isEditable(assignment: QuestionnaireAssignment): boolean {
    return assignment.status !== 'submitted' && !this.isOverdue(assignment);
  },

  /**
   * Log an event (audit trail)
   */
  async logEvent(data: Omit<QuestionnaireEvent, 'id' | 'createdAt'>): Promise<void> {
    await addDoc(collection(db, 'questionnaire_events'), {
      ...data,
      createdAt: Timestamp.now()
    });
  }
};

